import React, { useState, useEffect } from 'react';
import { useRouter } from 'next/router';
import { GuestUserLogin } from '@desquared/wave-components-library';
import postGuestCredentials from '@/api/guestCredentials';
import LoaderStore from '@/store/loader';
import GuestUserStore from '@/store/guestUser';
import { useTranslations } from 'next-intl';

import styles from './GuestLogin.module.css';

export default function GuestLogin({ logo, type, config }) {
  const [mobile, setMobile] = useState('');
  const [email, setEmail] = useState('');
  const [hasBottomError, setHasBottomError] = useState(false);
  const [bottomErrorText, setBottomErrorText] = useState('');
  const [emailErrorText, setEmailErrorText] = useState('');
  const [mobileErrorText, setMobileErrorText] = useState('');
  const [acceptedTerms, setHasAcceptedTerms] = useState(false);
  const t = useTranslations('guest_login');
  const r = useTranslations('common');
  const REQUIRED_FIELD = t('required_field');
  const userConfig = config?.fields?.user;

  const router = useRouter();

  useEffect(() => {
    const GuestUserStore$ = GuestUserStore.subscribe(_guestUser => {
      if (!_guestUser) return;
      setEmail(_guestUser.email);
      setMobile(_guestUser.contactPhone);
    });
    return () => {
      GuestUserStore$.unsubscribe();
    };
  }, []);

  const onEmailChange = e => {
    setEmail(e.currentTarget.value);
    setEmailErrorText('');
    setHasBottomError(false);
  };

  const onMobileChange = e => {
    setMobile(e.currentTarget.value);
    setHasBottomError(false);
  };

  function emailValidation() {
    if (!email) {
      setEmailErrorText(REQUIRED_FIELD);
      return false;
    }
    const _email = email.replace(/\s/g, '');
    const isValid = _email.match(
      // eslint-disable-next-line max-len
      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
    );

    if (!isValid) {
      setEmailErrorText(t('errors.invalid_email'));
    } else {
      setEmailErrorText('');
    }
    return isValid;
  }

  function phoneValidation() {
    if (!mobile) {
      setMobileErrorText(REQUIRED_FIELD);
      return false;
    }
    const numberIdNoSpace = mobile.replace(/\s/g, '');
    const isValid = numberIdNoSpace.match(/^\d{10}$/i);

    if (!isValid) {
      setMobileErrorText(t('errors.invalid_mobile'));
    } else {
      setMobileErrorText('');
    }
    return isValid;
  }

  async function onSubmitForm() {
    const isValidEmail = emailValidation();
    const isValidPhone = phoneValidation();
    if (isValidEmail && isValidPhone) {
      LoaderStore.addEvent('REGISTER_GUEST');
      const guestUserRequestData = {
        email,
        phone: mobile,
        termsAccepted: acceptedTerms,
      };

      const { error } = await postGuestCredentials(guestUserRequestData);
      LoaderStore.removeEvent('REGISTER_GUEST');

      if (error) {
        setBottomErrorText(error);
        setHasBottomError(true);
        return;
      }
      const guestUserData = {
        contactPhone: mobile,
        email,
        type: 'other',
      };
      GuestUserStore.update(guestUserData);
      router.push('/');
    }
  }

  return (
    <>
      <div className={styles.container}>
        <GuestUserLogin
          logo={logo}
          classOption={type === 1 || type === 2 ? styles.paddingContainer : styles.noPaddingContainer}
          logoRedirect={router.locale === router.defaultLocale ? '/' : `/${router.locale}`}
          mobilelLabelText={t('mobile_label')}
          titleText={t('title')}
          connectionText={t('connection_text')}
          emailPlaceHolderText={t('email_placeholder')}
          emailLabelText="Email *"
          mobilePlaceHolderText={t('email_placeholder')}
          connectText={t('connect_text')}
          guestText={t('guest_text')}
          bottomErrorText={bottomErrorText}
          acceptText={t('accept_text')}
          termsText={t('terms_text')}
          extraText={t('extra_text')}
          gdprText={t('gdpr_text')}
          hasAccountText={t('has_account_text')}
          onEmailChange={onEmailChange}
          onMobileChange={onMobileChange}
          mobileValue={mobile}
          emailValue={email}
          onSubmitForm={onSubmitForm}
          emailHelperTextType={emailErrorText ? 'error' : 'helper'}
          mobileHelperTextType={mobileErrorText ? 'error' : 'helper'}
          onGuestClick={() => {
            router.push('/login');
          }}
          isButtonDisabled={!acceptedTerms}
          hasBottomError={hasBottomError}
          gdprLink={process.env.gdpr}
          termsLink={process.env.terms}
          checkBoxChecked={acceptedTerms}
          checkCheckBox={() => setHasAcceptedTerms(!acceptedTerms)}
          minLengthErrorText={r('minimun_character')}
          patternField={r('invalid_input')}
          userConfig={userConfig}
        />
      </div>
    </>
  );
}
